/**
 * Customer Portal (UI) - shared datetime formatting helpers.
 *
 * Keeps UI date display consistent across routes while preserving raw UTC values
 * for backend sorting/filtering/query semantics.
 */

export type TimeDisplayMode = 'local' | 'utc';

/**
 * Resolve browser timezone in IANA format when available.
 */
export function getBrowserTimeZone(): string {
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return typeof browserZone === 'string' && browserZone.trim().length > 0 ? browserZone : 'UTC';
}

/**
 * Convert a Date instant into a local YYYY-MM-DD input value.
 */
export function toLocalDateInputValue(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Convert local date input (YYYY-MM-DD) into an ISO boundary instant.
 */
export function dateInputToIsoBoundary(value: string, boundary: 'start' | 'end'): string | null {
  const trimmed = value.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }
  const date =
    boundary === 'end'
      ? new Date(year, month - 1, day, 23, 59, 59, 999)
      : new Date(year, month - 1, day, 0, 0, 0, 0);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
}

function buildFormatOptions(
  mode: TimeDisplayMode,
  options?: Intl.DateTimeFormatOptions
): Intl.DateTimeFormatOptions {
  const merged: Intl.DateTimeFormatOptions = { ...(options ?? {}) };
  if (mode === 'utc') {
    merged.timeZone = 'UTC';
  }
  return merged;
}

export function getTimezoneContextLabel(mode: TimeDisplayMode): string {
  if (mode === 'utc') {
    return 'UTC';
  }
  return getBrowserTimeZone();
}

export function formatPortalDateTime(
  isoTimestamp: string,
  mode: TimeDisplayMode,
  options?: Intl.DateTimeFormatOptions
): string {
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) {
    return isoTimestamp;
  }
  const formatOptions = buildFormatOptions(mode, options);
  return parsed.toLocaleString(undefined, formatOptions);
}

export function formatPortalDateParts(
  isoTimestamp: string | null | undefined,
  mode: TimeDisplayMode
): { date: string; time: string } | null {
  if (typeof isoTimestamp !== 'string' || isoTimestamp.trim().length === 0) {
    return null;
  }
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) {
    return { date: isoTimestamp, time: '' };
  }
  const date = parsed.toLocaleDateString(
    undefined,
    buildFormatOptions(mode, {
      dateStyle: 'medium'
    })
  );
  const time = parsed.toLocaleTimeString(
    undefined,
    buildFormatOptions(mode, {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    })
  );
  return { date, time };
}
